import React, { useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react'; // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin!
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
// @ts-ignore
import moment, { Moment } from 'moment';
import { useAppContext } from '../../Services/AppContext';
import Tooltip from '../Popup/Tooltip';
import FullPageLoading from '../Utils/FullPageLoading/FullPageLoading';
import useTranslation from '../../Hooks/useTranslation';
import Loading from '../Loading';

interface ICalendar {
	onTimeClick?: any;
	onEventClick?: any;
	onEventHover?: any;
	onEventMouseOut?: any;
	headerToolbar?: any;
	initialView?: string;
	events?: any;
	resources?: any;
	allDaySlot?: boolean;
	allDaySlotText?: string;
	businessHours?: any;
	onDateChange?: (start: Moment, end?: Moment) => void;
	editable?: boolean;
	onEventDrop?: (event) => void;
	onEventResize?: (event) => void;
	loadingEvents?: boolean;
	views?: any;
	moveToDate?: Date;
	showMyEvents?: boolean;
	setShowMyEvents?: React.Dispatch<React.SetStateAction<boolean>>;
	renderEventItem?: (event: any) => React.ReactElement;
	eventDidMount?: () => void;
}

export default function Calendar(props: ICalendar) {
	const { language } = useAppContext();
	const { t } = useTranslation();

	const {
		headerToolbar = null,
		initialView = null,
		onTimeClick,
		onEventClick,
		onEventMouseOut,
		onEventHover,
		events = [],
		resources = [],
		allDaySlot = false,
		allDaySlotText = t('base.shift'),
		businessHours,
		onDateChange,
		editable = false,
		onEventDrop,
		onEventResize,
		loadingEvents = false,
		views,
		moveToDate,
		showMyEvents,
		setShowMyEvents,
		renderEventItem,
		eventDidMount,
	} = props;

	const calendarRef = useRef();
	const [showLoading, setShowLoading] = useState(false);
	const [isInitialized, setIsInitialized] = useState(true);

	// @ts-ignore
	useEffect(() => {
		if (!loadingEvents) {
			setTimeout(() => setShowLoading(false), 300);
		} else {
			setShowLoading(true);
		}
	}, [loadingEvents]);

	const handleDateClick = date => {
		const calendarApi = date.view.calendar;
		switch (date.view.type) {
			case 'timeGridDay':
			case 'timeGridWeek':
			case 'resourceTimeGridDay':
				onTimeClick && onTimeClick(moment(date.date), date.resource?.id);
				break;
			case 'dayGridMonth':
				calendarApi.changeView('timeGridDay', date.dateStr);
				break;
		}
	};

	const handleEventClick = event => {
		if (!event.event.allDay) {
			onEventClick && onEventClick(event);
		}
	};

	const handleEventMouseOut = () => {
		onEventMouseOut && onEventMouseOut();
	};

	const handleEventMouserOver = e => {
		// @ts-ignore
		let popup = Array.from(e.target.children).find(
			child => child.className === 'custom-event__popup',
		);
		if (popup) {
			popup.style.display = 'block';
		}
	};

	const handleEventMouserLeave = e => {
		// @ts-ignore
		let popup = Array.from(e.target.children).find(
			child => child.className === 'custom-event__popup',
		);
		if (popup) {
			popup.style.display = 'none';
		} else {
			if (e.target.className === 'custom-event__popup') {
				e.target.style.display = 'none';
			}
		}
	};

	const handleEventContent = args => {
		const event = args.event;

		if (renderEventItem) {
			return renderEventItem(event);
		}

		const eventUI = event.ui;
		let eventNote = event.extendedProps?.note
			? event.extendedProps?.note.substring(0, 250)
			: null;
		if (eventNote && eventNote != event.extendedProps.note) {
			eventNote = eventNote.concat('...');
		}

		if (event.extendedProps.workers) {
			return (
				<div
					className={'office-hours flex-column'}
					style={{ backgroundColor: event.backgroundColor }}>
					<div className={'d-flex flex-column'}>
						{event.extendedProps.workers.map((item, index) => (
							<Tooltip
								key={index}
								position='bottom center'
								trigger={
									<div className='office-hours__name'>
										<div>{item.name}</div>
										<div>{event?.extendedProps?.shift?.timeInterval}</div>
									</div>
								}>
								<div>
									<div>
										<span>
											{item.role.map((item, index2) => {
												return (
													<React.Fragment key={index2}>
														{index2 > 0 && <>, </>}
														{item?.role?.name}
													</React.Fragment>
												);
											})}
											:
										</span>{' '}
										{item.name}
									</div>
									{event.extendedProps.shift && (
										<div className={'office-hours__role'}>
											{event?.extendedProps?.shift.label}
										</div>
									)}
								</div>
							</Tooltip>
						))}
						{event.extendedProps.workers.length < 1 && (
							<div className='office-hours__name'>
								{event?.extendedProps?.shift?.label}
							</div>
						)}
					</div>
				</div>
			);
		} else {
			return (
				<>
					<Tooltip
						trigger={
							<div
								className={'custom-event'}
								style={{ backgroundColor: event.backgroundColor }}
								onMouseLeave={handleEventMouserLeave}
								onMouseEnter={handleEventMouserOver}>
								{event?.extendedProps?.createdByPatient && (
									<div className='custom-event__createByPatient' />
								)}
								<div className={'custom-event__name'}>{event.title}</div>
								{!!event?.extendedProps?.patient?.name && (
									<div className={'custom-event__user'}>
										{event.extendedProps?.patient?.name}
									</div>
								)}
								{event?.extendedProps?.doctor?.name && (
									<div className={'custom-event__user'}>
										{t('base.calendar.doctor') +
											': ' +
											event.extendedProps?.doctor?.name}
									</div>
								)}
							</div>
						}
						position='bottom center'>
						{!!event?.extendedProps?.patient && (
							<>
								<div className={'custom-event__date'}>
									{moment(event.start).format('HH:mm')}
									{event.end && (
										<>
											{' - '}
											{moment(event.end).format('HH:mm')}
										</>
									)}
								</div>
								<div className={'custom-event__name'}>
									{event?.extendedProps?.createdByPatient &&
										t('component.calendar.thePatientOrderedHimself') + ': '}
									{event.title}
								</div>
								<div className={'custom-event__detail__item'}>
									{event.extendedProps.meta &&
										event.extendedProps.meta.find(m => m.key === 'meetingType')
											?.value === 'onSite' &&
										t('base.meetingType.onSite')}
									{event.extendedProps.meta &&
										event.extendedProps.meta.find(m => m.key === 'meetingType')
											?.value === 'telemedicine' &&
										t('base.meetingType.telemedicine')}
								</div>
								<div className={'custom-event__detail__item'}>
									{event.extendedProps.patient.name &&
										event.extendedProps.patient.name + ', '}{' '}
									{event.extendedProps.patient.age &&
										event.extendedProps.patient.age +
											' ' +
											t('base.years.many')}
								</div>
								<div className={'custom-event__detail__item'}>
									{event.extendedProps.doctor.name &&
										t('base.calendar.doctor') +
											': ' +
											event.extendedProps.doctor.name}
								</div>
								<div className={'custom-event__detail__item'}>
									{event.extendedProps.patient.birthNumber &&
										t('base.shared.birthNumberShort') +
											': ' +
											event.extendedProps.patient.birthNumber}
								</div>
								<div className={'custom-event__detail__item'}>
									{event.extendedProps.patient.phone &&
										t('base.phoneShort') +
											': ' +
											event.extendedProps.patient.phone}
								</div>
							</>
						)}
						<div className={'custom-event__detail__item'}>
							{eventNote && t('base.shared.noteShort') + ': ' + eventNote}
						</div>
					</Tooltip>
				</>
			);
		}
	};

	const handleEventDidMount = (event: any) => {
		event.el.setAttribute('id', 'event-' + event.event.id);
		eventDidMount?.();
	};

	const goToNextDay = () => {
		const calendarApi = calendarRef.current.getApi();
		calendarApi.next();
	};

	const goToPreviousDay = () => {
		const calendarApi = calendarRef.current.getApi();
		calendarApi.prev();
	};

	const handleDatesSet = e => {
		const start = moment(e.start);
		const end = moment(e.end);

		onDateChange && onDateChange(start, start === end ? null : end);
	};

	const handleEventDrop = e => {
		onEventDrop && onEventDrop(e.event);
	};

	const handleEventResize = e => {
		onEventResize && onEventResize(e.event);
	};

	useEffect(() => {
		if (moveToDate) {
			const calendarApi = calendarRef.current.getApi();
			calendarApi.gotoDate(moveToDate);
			calendarApi.changeView('timeGridDay');
		}
	}, [moveToDate]);

	const handleRedrawCalendar = () => {
		setIsInitialized(false);
		setIsInitialized(true);
	};

	useEffect(() => {
		const element = document.querySelector('#sidemenu-toggle');
		element.addEventListener('click', handleRedrawCalendar);
		return () => {
			element.removeEventListener('click', handleRedrawCalendar);
		};
	}, []);

	if (!isInitialized) {
		return <></>;
	}

	return (
		<div style={{ height: '100%' }}>
			{showLoading && <FullPageLoading />}
			{/* @ts-ignore */}
			<FullCalendar
				ref={calendarRef}
				schedulerLicenseKey={'0784609858-fcs-1664878357'}
				plugins={[
					resourceTimeGridPlugin,
					dayGridPlugin,
					timeGridPlugin,
					interactionPlugin,
				]}
				initialView={initialView ? initialView : 'dayGridMonth'}
				locale={language}
				slotDuration='00:30:00'
				slotLabelFormat={{
					hour: '2-digit',
					minute: '2-digit',
					omitZeroMinute: false,
					meridian: false,
				}}
				slotLabelInterval={{
					minutes: 1,
				}}
				headerToolbar={
					headerToolbar ?? {
						left: 'prev,next,today',
						center: 'title',
						right: `${
							showMyEvents !== undefined && setShowMyEvents !== undefined
								? 'showMyEvents,'
								: ''
						}timeGridDay,timeGridWeek,dayGridMonth`,
					}
				}
				dateClick={handleDateClick}
				eventClick={handleEventClick}
				datesSet={handleDatesSet}
				allDaySlot={allDaySlot}
				allDayText={allDaySlotText}
				resources={resources}
				businessHours={businessHours}
				events={events}
				eventShortHeight={0}
				firstDay={1}
				nowIndicator={true}
				buttonText={{
					today: t('base.shared.now'),
					month: t('base.months.one'),
					week: t('base.weeks.one'),
					day: t('base.days.one'),
					list: t('base.shared.list'),
				}}
				eventContent={handleEventContent}
				customButtons={{
					prev: { click: () => goToPreviousDay() },
					next: { click: () => goToNextDay() },
					showMyEvents: {
						text: showMyEvents
							? t('base.calendar.showAll')
							: t('base.calendar.showMine'),
						click: () => setShowMyEvents?.((prev: boolean) => !prev),
					},
				}}
				editable={editable}
				eventDrop={handleEventDrop}
				eventResize={handleEventResize}
				eventDidMount={handleEventDidMount}
				views={views}
				eventMouseLeave={handleEventMouseOut}
			/>
		</div>
	);
}
